<template>
  <vis-load-user-config v-if="!loaded" @loaded="onLoaded" />
  <div class="app" v-else-if="access.granted">
    <AppHeader
      class="app__header"
      fixed
    >
      <SidebarToggler class="d-lg-none vis-ios-visible" display="md" mobile />
      <SidebarToggler class="d-md-down-none vis-ios-hidden" display="lg" />
      <b-link class="navbar-brand" to="/">
        <img
          class="navbar-brand-full"
          src="/img/brand/white/logo.svg"
          width="210"
          height="45"
          alt="Visary"
        />
        <img
          class="navbar-brand-minimized"
          src="/img/brand/white/logo-symbol.svg"
          width="32"
          height="32"
          alt="Visary"
        />
      </b-link>

      <b-navbar-nav class="ml-auto">
        <div
          class="app__position-info"
          v-if="userPositionInfo"
        >{{ userPositionInfo }}</div>
        <b-nav-item class="d-md-down-none">
          <i
            :class="{
              'far fa-expand': !fullscreen,
              'far fa-compress': fullscreen,
            }"
            @click="toggleFullscreen"
          ></i>
        </b-nav-item>
        <vis-header-dropdown-accnt />
      </b-navbar-nav>
      <vis-aside-toggler />
    </AppHeader>

    <div class="app-body">
      <AppSidebar fixed>
        <SidebarHeader />
        <SidebarForm />
        <vis-menu />
        <SidebarFooter />
        <SidebarMinimizer />
      </AppSidebar>
      <main class="main">
        <VisBreadcrumb :list="breadcrumb" />
        <div class="container-fluid">
          <router-view></router-view>
        </div>
      </main>
      <AppAside fixed>
        <vis-aside-tabs />
      </AppAside>
    </div>

    <TheFooter>
      <div class="developer ml-auto">
        <img src="/img/logo.png" alt="Visary UI" height="25" />
      </div>
    </TheFooter>

    <VisScreensMalfunctionNotification
      class="app__screens-notification"
      v-if="isScreensNotificationVisible"
      @closeNotification="closeScreensNotification"
    />
  </div>
</template>

<script>
import {
  Header as AppHeader,
  SidebarToggler,
  Sidebar as AppSidebar,
  SidebarFooter,
  SidebarForm,
  SidebarHeader,
  SidebarMinimizer,
  Aside as AppAside,
  Footer as TheFooter,
} from "@coreui/vue";

import VisAsideTabs from "./vis-aside-tabs";
import VisBreadcrumb from "./vis-breadcrumb";
import VisScreensMalfunctionNotification from './vis-screens-malfunction-notification';
import VisHeaderDropdownAccnt from "./vis-header-dropdown-accnt";
import VisMenu from "@/views/vis-menu";
import VisLoadUserConfig from "@/views/vis-load-user-config";
import VisAsideToggler from "./vis-aside-toggler";
import api from "@/api/visary-api";
import navUtils from "@/shared/nav-utils";
import globalState from "@/shared/global-state";
import requestService from "@/api/csdn-api/request-service";
import monitoring from "@/api/web-api/monitoring";
import jsCookie from 'js-cookie'

var doc = document;
var docEl = doc.documentElement;

const INDIVIDUAL_POSTADDRESS = 'Individual'
const COMPANY_POSTADDRESS = 'Company'
const SCREENS_NOTIFICATION_COOKIE_NAME = 'rgm_screens_notification'
const SCREENS_NOTIFICATION_DELAY = 10 * 60 * 1000 // 10 minutes
const MALFUNCTION_SCREEN_COLOR = 'black'

export default {
  name: "vis-layout",
  components: {
    AppHeader,
    AppSidebar,
    AppAside,
    TheFooter,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    SidebarMinimizer,
    VisAsideTabs,
    VisHeaderDropdownAccnt,
    VisMenu,
    VisAsideToggler,
    VisLoadUserConfig,
    VisBreadcrumb,
    VisScreensMalfunctionNotification,
  },
  data() {
    return {
      loaded: false,
      access: { granted: false },
      fullscreen: false,
      isScreensNotificationVisible: false,
      screensMonitoringTimeout: null,
    };
  },
  computed: {
    pageName() {
      return this.$route.name;
    },
    breadcrumb() {
      return this.$route.matched.filter(
        (route) => route.meta && route.meta.label
      );
    },
    userPositionInfo() {
      if (!globalState || !globalState.currentUser) return ''

      const { currentUser } = globalState
      let info = ''

      if (currentUser.employeeData?.Department?.Title) {
        if (currentUser.groupsData?.length) info += `${currentUser.groupsData.map(g => g.title).join(', ')}, `
        info += `${currentUser.employeeData.Department.Title}, `
      }

      let userName = `${currentUser.personCard?.LastName} `

      if (currentUser.personCard?.FirstName) {
        userName += `${currentUser.personCard.FirstName[0]}.`

        if (currentUser.personCard.MiddleName) userName += `${currentUser.personCard.MiddleName[0]}.`
      }

      if (!currentUser.employeeData?.Department) {
        if (currentUser.Correspondent?.Title) {
          info += `${currentUser.Correspondent.Title}, `
        } else {
          if (currentUser.personCard?.Organizations) {
            try {
              const organization = JSON.parse(currentUser.personCard.Organizations)
              info += `${organization.ShortName || organization.FullName}, `
            } catch(e) {
              info += 'Физ.лицо, '
            }
          } else {
            info += 'Физ.лицо, '
          }
        }
      }

      info += userName

      return info
    },
  },
  async mounted() {
    await this.setScreensMonitoring()
  },
  methods: {
    closeScreensNotification() {
      this.isScreensNotificationVisible = false
    },
    showScreensNotification() {
      this.isScreensNotificationVisible = true
    },
    onLoaded() {
      this.loaded = true;
      this.ensureAccessGranted();
    },

    ensureAccessGranted() {
      api.preset
        .tryGetAppPreset()
        .then((_) => {
          this.access.granted = true;
        })
        .catch((error) => {
          navUtils.navigateToAccessDenied(this.$router);
        });
    },

    isFullscreen() {
      return (
        !!doc.fullscreenElement ||
        !!doc.mozFullScreenElement ||
        !!doc.webkitFullscreenElement
      );
    },

    toggleFullscreen() {
      if (!this.isFullscreen()) {
        if (docEl.requestFullscreen) {
          docEl.requestFullscreen();
        } else if (docEl.mozRequestFullScreen) {
          docEl.mozRequestFullScreen();
        } else if (docEl.webkitRequestFullscreen) {
          docEl.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
        }
        this.fullscreen = true;
      } else {
        if (doc.cancelFullScreen) {
          doc.cancelFullScreen();
        } else if (doc.mozCancelFullScreen) {
          doc.mozCancelFullScreen();
        } else if (doc.webkitCancelFullScreen) {
          doc.webkitCancelFullScreen();
        }
        this.fullscreen = false;
      }
    },

    positionSuccess(geo) {
      globalState.geolocationCoordinates = geo;
    },

    positionError(geo) {
    },
    async setScreensMonitoring() {
      if (this.screensMonitoringTimeout) clearTimeout(this.screensMonitoringTimeout)

      const screensTimestamp = await jsCookie.get(SCREENS_NOTIFICATION_COOKIE_NAME)

      if (!screensTimestamp || Date.now() - screensTimestamp > SCREENS_NOTIFICATION_DELAY) {
        this.checkScreensStatus()
      }

      this.screensMonitoringTimeout = setTimeout(() => {
        this.setScreensMonitoring()
      }, SCREENS_NOTIFICATION_DELAY)
    },
    async checkScreensStatus() {
      try {
        const { data: { items } } = await monitoring.getScreens();

        if (!items || !items.every(this.checkScreen)) {
          this.setScreensCookie()
          this.showScreensNotification()

          return
        }
        await requestService.getAspurScreen()
      } catch(e) {
        this.setScreensCookie()
        this.showScreensNotification()
      }
    },
    setScreensCookie() {
      jsCookie.set(SCREENS_NOTIFICATION_COOKIE_NAME, Date.now())
    },
    checkScreen(screen) {
      return screen.metrics && screen.metrics.values && screen.metrics.values[0] && screen.metrics.values[0].color !== MALFUNCTION_SCREEN_COLOR
    },
  },

  created() {
    this.fullscreen = this.isFullscreen();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(this.positionSuccess, this.positionError);
    }
  },
};
</script>

<style lang="scss" scoped>
.app {
  &__header {
    flex-wrap: nowrap;
  }
  &__position-info {
    margin: 0 16px;
    color: white;
  }
}
</style>
